import React from 'react';
import {
  Box,
  Chip,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import DescriptionIcon from '@mui/icons-material/Description';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BrandLogo from './BrandLogo';

const updatedAt = 'July 16th, 2026';

const sections = [
  {
    title: 'Information We Collect',
    body: [
      'Account information such as driver name, work email, role, and login activity.',
      'Driver profile documents such as TDG certificate details and driver license scans.',
      'Operational records including pre-trip inspections, post-trip inspections, defect reports, fuel logs, trip logs, odometer values, vehicle selections, notes, and related photos.',
      'Vehicle glove box documents shown to drivers for roadside and operational access.',
      'Basic device/app information needed for authentication, security, troubleshooting, and notifications.',
    ],
  },
  {
    title: 'How We Use Information',
    body: [
      'To provide BioFleet Driver features for Biomed Recovery & Disposal Ltd drivers and administrators.',
      'To support driver check-ins, trip logging, fuel logging, vehicle document access, and administrative review.',
      'To make required vehicle and driver documents available in the mobile app, including offline access where supported.',
      'To maintain safety, audit, security, compliance, and operational records.',
      'To improve reliability, diagnose issues, and support authorized users.',
    ],
  },
  {
    title: 'Sharing And Access',
    body: [
      'Information is available only to authorized Biomed Recovery & Disposal Ltd personnel and approved service providers who help operate BioFleet.',
      'Driver activity and submitted records may be visible to administrators for operational, compliance, and safety purposes.',
      'We do not sell driver personal information.',
    ],
  },
  {
    title: 'Photos, Documents, And Offline Access',
    body: [
      'Drivers may upload inspection photos and license scans. Global and IT administrators may upload or replace user profile photographs, license scans, TDG certificates, and vehicle glove box documents.',
      'Vehicle glove box documents may be cached on the mobile device for offline access while a vehicle is on road.',
      'Drivers should protect their device because cached documents may remain available inside the app until refreshed, cleared, or logged out.',
    ],
  },
  {
    title: 'Retention And Security',
    body: [
      'Records are retained as needed for business operations, safety, maintenance, compliance, audit, and legal purposes.',
      'BioFleet uses access controls, authenticated sessions, and role-based permissions to limit access to authorized users.',
      'No system can guarantee absolute security, but reasonable safeguards are used to protect company and driver information.',
    ],
  },
  {
    title: 'Contact',
    body: [
      'For privacy questions or document correction requests, contact Biomed Recovery & Disposal Ltd at info@biomedwaste.net.',
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <Box
      sx={{
        minHeight: '100dvh',
        py: { xs: 3, md: 6 },
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            overflow: 'hidden',
            borderRadius: 4,
            border: '1px solid rgba(0,31,63,0.1)',
            boxShadow: '0 24px 70px rgba(0,31,63,0.12)',
          }}
        >
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              background: 'linear-gradient(135deg, #001f3f 0%, #123b83 100%)',
              color: '#fff',
            }}
          >
            <BrandLogo
              light
              logoPlate
              textColor="#ffffff"
              subtitleColor="rgba(255,255,255,0.76)"
              sx={{ mb: 3 }}
            />
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Chip icon={<SecurityIcon />} label="Privacy Policy" sx={{ bgcolor: '#d7ef23', fontWeight: 900 }} />
              <Chip icon={<LocalShippingIcon />} label="BioFleet Driver" sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: '#fff', fontWeight: 800 }} />
            </Stack>
            <Typography variant="h3" sx={{ fontWeight: 950, letterSpacing: -1 }}>
              BioFleet Driver Privacy Policy
            </Typography>
            <Typography sx={{ mt: 1.5, color: 'rgba(255,255,255,0.82)', fontWeight: 700 }}>
              Specifically prepared for Biomed Recovery & Disposal Ltd drivers, fleet administrators, and authorized operational users.
            </Typography>
            <Typography sx={{ mt: 2, color: 'rgba(255,255,255,0.74)' }}>
              Last updated: {updatedAt}
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 3, md: 4 } }}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 950, color: '#001f3f' }}>
                  Overview
                </Typography>
                <Typography sx={{ mt: 1, color: '#334155', lineHeight: 1.75 }}>
                  BioFleet Driver is a company operations app used by Biomed Recovery & Disposal Ltd to support driver check-ins,
                  trip activity, vehicle document access, driver documents, and fleet compliance workflows.
                </Typography>
              </Box>

              {sections.map((section) => (
                <Box key={section.title}>
                  <Divider sx={{ mb: 2 }} />
                  <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1 }}>
                    <DescriptionIcon sx={{ color: '#123b83' }} />
                    <Typography variant="h6" sx={{ fontWeight: 950, color: '#001f3f' }}>
                      {section.title}
                    </Typography>
                  </Stack>
                  <Stack component="ul" spacing={1} sx={{ m: 0, pl: 3, color: '#334155' }}>
                    {section.body.map((item) => (
                      <Typography component="li" key={item} sx={{ lineHeight: 1.65 }}>
                        {item}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              ))}

              <Box sx={{ bgcolor: '#eef6ff', borderRadius: 3, p: 2.5, border: '1px solid #c7ddff' }}>
                <Typography sx={{ fontWeight: 900, color: '#001f3f' }}>
                  Public URL for Google Play:
                </Typography>
                <Typography sx={{ mt: 0.5, wordBreak: 'break-all', color: '#123b83', fontWeight: 800 }}>
                  https://biofleet.biomedwaste.net/privacy
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
